/**
 * The Focus scope in the URL: which apps, which dates and which filters a
 * screen looks at. The URL is the WHOLE scope, never a patch (parity §0
 * "Shared filter scope"): every key absent from the URL is at its default.
 *
 * Keys are apps/web's (`utils/filterQuery.ts`), so links from the current app
 * (`/asset-performance?…`, redirected with the query intact) keep working,
 * plus four additions:
 *  - `status`  — asset status filter (scaling, steady, fatiguing, new,
 *                inactive, not_enough_data), applied by the screen, not the API;
 *  - `all_apps=1` — the explicit portfolio view ("All apps"); without it and
 *                without `app_id`, the screen resolves the user's Focus;
 *  - `period`  — the named date shortcut ("last_14"), so the date chip can
 *                say "Last 14 days" and a link can mean "the latest 14 days";
 *  - thresholds are integers where the API expects integers.
 *
 * Every screen uses the SAME keys, so a scope carries across screens. A
 * screen reads only the keys its controls offer (`FocusControls`): a key it
 * does not offer is dropped, exactly like an invalid value. Screens add their
 * own keys next to these (see `FocusConfig.parse`).
 *
 * Invalid values are dropped, following apps/web's parsing rules: lists are
 * de-duplicated and emptied of blanks, `asset_type` and `status` are checked
 * against their enums, both dates must be valid with from ≤ to (or both are
 * ignored), thresholds must be finite and ≥ 0 (0 means "no minimum"), and the
 * activity filter is on only for `active_during_past_n_hours=24`.
 */
import type { FocusControls } from './config';
import { ASSET_STATUSES } from '@/components/ui/tag';
import { param, type RawSearch } from '@/router/search';

export const STATUS_FILTERS = ASSET_STATUSES;
export type StatusFilter = (typeof STATUS_FILTERS)[number];

/** `AssetTypeEnum` of the API; the panel offers the first four, a URL may carry any. */
export const ASSET_TYPES = ['image', 'text', 'video', 'playable', 'site_link'] as const;
export type AssetType = (typeof ASSET_TYPES)[number];

export const PERIODS = [
  'last_7',
  'last_14',
  'last_28',
  'this_week',
  'last_week',
  'this_month',
  'last_month',
  'month_to_date',
  'lifetime',
] as const;
export type Period = (typeof PERIODS)[number];

/** List filters sent to the API under the same key. */
export const LIST_KEYS = ['campaign_name', 'ad_group_name', 'ad_group_ad_name', 'ad_network_account_id', 'country_code', 'platform', 'os'] as const;
export type ListKey = (typeof LIST_KEYS)[number];

export const THRESHOLD_KEYS = ['min_impressions', 'min_clicks', 'min_installs', 'min_cost'] as const;
export type ThresholdKey = (typeof THRESHOLD_KEYS)[number];

export const ACTIVE_HOURS = 24;

/** Query params accepted by the `/api/admaster/asset/*` endpoints (see
 * apps/api/api/routes/admaster/utils.py `get_performance_query_params`). Keys
 * and value encoding are the API's own: repeated keys become arrays. */
export type FocusApiParams = Record<string, string | string[]>;

/** The typed scope. Field names ARE the URL keys (the router writes them as is). */
export interface FocusSearch {
  app_id: string[];
  all_apps?: '1' | undefined;
  date_from?: string | undefined;
  date_to?: string | undefined;
  period?: Period | undefined;
  status: StatusFilter[];
  asset_type: AssetType[];
  campaign_name: string[];
  ad_group_name: string[];
  ad_group_ad_name: string[];
  ad_network_account_id: string[];
  country_code: string[];
  platform: string[];
  os: string[];
  min_impressions?: number | undefined;
  min_clicks?: number | undefined;
  min_installs?: number | undefined;
  min_cost?: number | undefined;
  active_during_past_n_hours?: typeof ACTIVE_HOURS | undefined;
}

/** What the Filters panel edits: everything except the dates. */
export type FocusFilters = Omit<FocusSearch, 'date_from' | 'date_to' | 'period'>;

/** A positive threshold, or undefined (0 and anything invalid mean "no minimum"). */
const threshold = (raw: RawSearch, key: ThresholdKey): number | undefined => {
  const value = param.number(raw, key, { min: 0, integer: key !== 'min_cost' });
  return value && value > 0 ? value : undefined;
};

/**
 * Read the scope keys a screen offers. Keys are returned in one fixed order,
 * which is the order they appear in the URL (stable, shareable URLs).
 */
export function parseFocusSearch(raw: RawSearch, controls: FocusControls): FocusSearch {
  const filters = controls.filters || null;
  const allApps = controls.allApps && param.flag(raw, 'all_apps', '1');
  const dates = controls.dates ? param.dateRange(raw, 'date_from', 'date_to') : undefined;
  const list = (key: ListKey): string[] => (filters ? param.list(raw, key) : []);
  const min = (key: ThresholdKey): number | undefined => (filters?.thresholds ? threshold(raw, key) : undefined);
  return {
    // Portfolio mode names no app; a stray app_id next to it is ignored.
    app_id: allApps ? [] : param.list(raw, 'app_id'),
    all_apps: allApps ? '1' : undefined,
    date_from: dates?.from,
    date_to: dates?.to,
    period: controls.dates ? param.enumValue(raw, 'period', PERIODS) : undefined,
    status: filters?.status ? param.enumList(raw, 'status', STATUS_FILTERS) : [],
    asset_type: filters ? param.enumList(raw, 'asset_type', ASSET_TYPES) : [],
    campaign_name: list('campaign_name'),
    ad_group_name: list('ad_group_name'),
    ad_group_ad_name: list('ad_group_ad_name'),
    ad_network_account_id: list('ad_network_account_id'),
    country_code: list('country_code'),
    platform: list('platform'),
    os: list('os'),
    min_impressions: min('min_impressions'),
    min_clicks: min('min_clicks'),
    min_installs: min('min_installs'),
    min_cost: min('min_cost'),
    active_during_past_n_hours:
      filters?.activity && param.number(raw, 'active_during_past_n_hours') === ACTIVE_HOURS ? ACTIVE_HOURS : undefined,
  };
}

export const EMPTY_FILTERS: FocusFilters = {
  app_id: [],
  all_apps: undefined,
  status: [],
  asset_type: [],
  campaign_name: [],
  ad_group_name: [],
  ad_group_ad_name: [],
  ad_network_account_id: [],
  country_code: [],
  platform: [],
  os: [],
  min_impressions: undefined,
  min_clicks: undefined,
  min_installs: undefined,
  min_cost: undefined,
  active_during_past_n_hours: undefined,
};

export const filtersOf = (search: FocusSearch): FocusFilters => {
  const filters: FocusFilters = { ...EMPTY_FILTERS };
  for (const key of Object.keys(EMPTY_FILTERS) as (keyof FocusFilters)[]) {
    (filters as unknown as Record<string, unknown>)[key] = search[key];
  }
  return filters;
};

/**
 * Query params for `/api/admaster/asset/*` and `/overview/*`, in the API's
 * own encoding. `status` is not an API filter: the screen applies it
 * (Performance's table half reads `scope.apiParams.status`). Endpoints ignore
 * keys they don't take.
 */
export function toApiParams(search: Partial<FocusSearch>, options: { dates?: boolean } = {}): FocusApiParams {
  const out: FocusApiParams = {};
  if (!search.all_apps && search.app_id?.length) out.app_id = [...search.app_id];
  if (options.dates !== false && search.date_from && search.date_to) {
    out.date_from = search.date_from;
    out.date_to = search.date_to;
  }
  if (search.asset_type?.length) out.asset_type = [...search.asset_type];
  for (const key of LIST_KEYS) {
    const values = search[key];
    if (values?.length) out[key] = [...values];
  }
  for (const key of THRESHOLD_KEYS) {
    const value = search[key];
    if (value !== undefined && value > 0) out[key] = String(value);
  }
  if (search.active_during_past_n_hours === ACTIVE_HOURS) out.active_during_past_n_hours = String(ACTIVE_HOURS);
  if (search.status?.length) out.status = [...search.status];
  return out;
}

/** A stable identity for a set of params (sorted keys and values). */
export const paramsKey = (params: FocusApiParams): string =>
  JSON.stringify(
    Object.keys(params)
      .sort()
      .map((key) => {
        const value = params[key];
        return [key, Array.isArray(value) ? [...value].sort() : value];
      }),
  );

/** Two filter states are the same when they would ask the API the same question. */
export const sameFilters = (a: FocusFilters, b: FocusFilters): boolean =>
  a.all_apps === b.all_apps && paramsKey(toApiParams(a, { dates: false })) === paramsKey(toApiParams(b, { dates: false }));

/** Filters in the "More filters" section (opened automatically when any is active). */
export const moreFiltersCount = (f: FocusFilters): number =>
  [f.asset_type.length, f.country_code.length, f.platform.length, f.os.length].filter(Boolean).length +
  (f.active_during_past_n_hours ? 1 : 0) +
  THRESHOLD_KEYS.filter((key) => f[key] !== undefined).length;

/** Active filters, as counted on the Filters chip. Apps and dates have their own controls. */
export const activeFiltersCount = (f: FocusFilters): number =>
  [f.status.length, f.ad_network_account_id.length, f.campaign_name.length, f.ad_group_name.length, f.ad_group_ad_name.length].filter(Boolean).length +
  moreFiltersCount(f);
